import React, { createContext, useContext, useEffect, useState } from 'react';
import { UserTier } from '../types/client';
import { isRouteAllowed } from '../config/tierConfig';

interface UserTierContextType {
  userTier: UserTier;
  isLoading: boolean;
  error: string | null;
  setUserTier: (tier: UserTier) => void;
  hasFeatureAccess: (featureId: string) => boolean;
  hasRouteAccess: (route: string) => boolean;
}

const UserTierContext = createContext<UserTierContextType | undefined>(undefined);

export const useUserTierContext = () => {
  const context = useContext(UserTierContext);
  if (context === undefined) {
    throw new Error('useUserTierContext must be used within a UserTierProvider');
  }
  return context;
};

interface UserTierProviderProps {
  children: React.ReactNode;
}

export const UserTierProvider: React.FC<UserTierProviderProps> = ({ children }) => {
  const [userTier, setUserTierState] = useState<UserTier>('free');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Simulate fetching user tier from backend/auth system
  useEffect(() => {
    const fetchUserTier = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // In a real application, you would:
        // 1. Get user ID from authentication context
        // 2. Make API call to fetch user's subscription/tier
        // 3. Handle authentication state changes
        
        // For demo purposes, check if user has a stored tier preference
        const storedTier = localStorage.getItem('userActualTier');
        
        // Remove artificial delay for better UX
        // await new Promise(resolve => setTimeout(resolve, 800));

        // First, try to detect tier from current URL path
        const currentPath = window.location.pathname;
        let pathDetectedTier: UserTier | null = null;
        
        if (currentPath.startsWith('/free')) {
          pathDetectedTier = 'free';
        } else if (currentPath.startsWith('/smb')) {
          pathDetectedTier = 'smb';
        } else if (currentPath.startsWith('/professional')) {
          pathDetectedTier = 'professional';
        } else if (currentPath.startsWith('/medium')) {
          pathDetectedTier = 'professional'; // Legacy support
        } else if (currentPath.startsWith('/enterprise')) {
          pathDetectedTier = 'enterprise';
        }

        // Check localStorage for stored tier
        const storedTier2 = localStorage.getItem('userTier');
        let finalTier: UserTier = 'free'; // default to free
        
        // Priority: URL path > localStorage > legacy mapping > default
        if (pathDetectedTier) {
          finalTier = pathDetectedTier;
        } else if (storedTier && ['free', 'smb', 'professional', 'enterprise'].includes(storedTier)) {
          finalTier = storedTier as UserTier;
        } else if (storedTier2 && ['free', 'smb', 'professional', 'enterprise'].includes(storedTier2)) {
          finalTier = storedTier2 as UserTier;
        } else if (storedTier === 'medium' || storedTier2 === 'medium') {
          finalTier = 'professional'; // Map legacy "medium" tier to "professional"
        } else if (storedTier === 'premium' || storedTier2 === 'premium') {
          finalTier = 'professional'; // Map legacy "premium" tier to "professional"
        } else {
          finalTier = 'free'; // Default to Free
        }
        
        // Sync both localStorage keys
        const beforeSync = {
          userTier: localStorage.getItem('userTier'),
          userActualTier: localStorage.getItem('userActualTier')
        };
        
        localStorage.setItem('userActualTier', finalTier);
        localStorage.setItem('userTier', finalTier);
        
        console.log('🔄 CONTEXT TIER SYNC:', { 
          currentPath,
          pathDetectedTier,
          storedTier, 
          storedTier2, 
          finalTier,
          beforeSync,
          afterSync: {
            userTier: localStorage.getItem('userTier'),
            userActualTier: localStorage.getItem('userActualTier')
          },
          synced: 'UserTierContext synced to ' + finalTier,
          WARNING: beforeSync.userTier !== finalTier ? `⚠️ CHANGED FROM ${beforeSync.userTier} TO ${finalTier}` : '✅ No change'
        });
        
        setUserTierState(finalTier);
      } catch (err) {
        setError('Failed to load user tier');
        setUserTierState('free'); // Fallback to Free
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserTier();
  }, []);

  const setUserTier = (tier: UserTier) => {
    setUserTierState(tier);
    localStorage.setItem('userActualTier', tier);
    
    // In a real app, you would also:
    // 1. Make API call to update user's subscription
    // 2. Handle payment processing
    // 3. Update user profile in database
  };

  const hasFeatureAccess = (featureId: string): boolean => {
    const tierFeatures: Record<UserTier, string[]> = {
      free: ['dashboard', 'ai-assistant-basic'],
      smb: ['dashboard', 'campaigns-basic', 'studio-basic', 'reports-basic', 'ai-assistant-basic'],
      professional: [
        'dashboard', 'campaigns-basic', 'studio-basic', 'reports-basic', 'ai-assistant-basic',
        'campaigns-advanced', 'team-collaboration', 'analytics-enhanced', 'automations-basic', 'integrations-hub', 'ai-assistant-pro'
      ],
      enterprise: [
        'dashboard', 'campaigns-basic', 'studio-basic', 'reports-basic', 'ai-assistant-basic',
        'campaigns-advanced', 'team-collaboration', 'analytics-enhanced', 'automations-basic', 'integrations-hub', 'ai-assistant-pro',
        'white-label', 'api-access', 'sso-security', 'dedicated-support', 'custom-reporting', 'unlimited-ai'
      ]
    };

    return tierFeatures[userTier]?.includes(featureId) || false;
  };

  const hasRouteAccess = (route: string): boolean => {
    return isRouteAllowed(userTier, route);
  };

  const value: UserTierContextType = {
    userTier,
    isLoading,
    error,
    setUserTier,
    hasFeatureAccess,
    hasRouteAccess
  };

  return (
    <UserTierContext.Provider value={value}>
      {children}
    </UserTierContext.Provider>
  );
};
